import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  timeout: 30000
})

api.interceptors.request.use(config => {
  const token = localStorage.getItem('markjy_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Suppress uncaught timeout errors
api.interceptors.response.use(
  res => res,
  err => {
    if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
      console.warn('[API] request timed out:', err.config.url)
    }
    return Promise.reject(err)
  }
)

// Get or create client token (UUID for temp username tracking)
function getClientToken() {
  let token = localStorage.getItem('markjy_client_token')
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)
    localStorage.setItem('markjy_client_token', token)
  }
  return token
}

// Auth
export const register = (username, password) =>
  api.post('/auth/register', { username, password })

export const login = (username, password) =>
  api.post('/auth/login', { username, password })

export const checkAuth = () =>
  api.get('/auth/check')

export const checkUsername = (username) =>
  api.post('/auth/check-username', { username })

export const checkUsernameOnPost = (postId, username) =>
  api.post(`/auth/check-username/${postId}`, { username, client_token: getClientToken() })

// Posts
export const getPosts = (page = 1, limit = 10) =>
  api.get('/posts', { params: { page, limit } })

export const getPost = (id) =>
  api.get(`/posts/${id}`)

// Comments
export const getComments = (postId) =>
  api.get(`/posts/${postId}/comments`)

export const createComment = (postId, username, content) =>
  api.post(`/posts/${postId}/comments`, { username, content, client_token: getClientToken() })

export const replyComment = (commentId, username, content) =>
  api.post(`/comments/${commentId}/replies`, { username, content, client_token: getClientToken() })

// Comment like
export const toggleCommentLike = (id) => api.post(`/comments/${id}/like`)
// Comment pin (admin)
export const toggleCommentPin = (id) => api.post(`/admin/comments/${id}/pin`)
// Comment info (admin)
export const getCommentInfo = (id) => api.get(`/admin/comments/${id}/info`)
// Ban IP (admin)
export const banIP = (ipHash, duration) => api.post('/admin/ban-ip', { ip_hash: ipHash, duration })
// Unban IP (admin)
export const unbanIP = (ipHash) => api.delete('/admin/ban-ip', { params: { ip: ipHash } })

// Like
export const toggleLike = (id) => api.post(`/posts/${id}/like`)

// About
export const getAboutSettings = () => api.get('/settings/about')
export const saveAboutSettings = (content) => api.put('/admin/settings/about', { value: content })
// Contacts
export const getContactsSettings = () => api.get('/settings/contacts')
export const saveContactsSettings = (contacts) => api.put('/admin/settings/contacts', { value: JSON.stringify(contacts) })

// Stats
export const getPostStats = (id, days = 30) => api.get(`/posts/${id}/stats`, { params: { days } })

// Admin
export const getAdminInfo = () =>
  api.get('/admin/info')

export const createPost = (title, content, options = {}) =>
  api.post('/admin/posts', { title, content, ...options })

export const updatePost = (id, title, content, options = {}) =>
  api.put(`/admin/posts/${id}`, { title, content, ...options })

export const deletePost = (id) =>
  api.delete(`/admin/posts/${id}`)

export const deleteComment = (id) =>
  api.delete(`/admin/comments/${id}`)

export const changePassword = (oldPassword, newPassword) =>
  api.put('/admin/password', { old_password: oldPassword, new_password: newPassword })

export const uploadImage = (file) => {
  const formData = new FormData()
  formData.append('image', file)
  return api.post('/admin/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export default api
